/* global gsap, ScrollTrigger */
(function () {
    'use strict';

    // Ensure GSAP + ScrollTrigger are present
    if (!window.gsap || !window.ScrollTrigger) {
        if (window && window.console && console.warn) {
            console.warn('[anim-engine] GSAP or ScrollTrigger not found. Load them before anim-engine.js');
        }
        return;
    }

    var _gsap = window.gsap;
    var _ScrollTrigger = window.ScrollTrigger;
    _gsap.registerPlugin(_ScrollTrigger);

    var mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    function isReduced() {
        return mqReduced && mqReduced.matches;
    }
    function isMobile() {
        return window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    }

    // Read a CSS var from :root; convert times to seconds when appropriate
    function cssVar(name, fallback) {
        var val = getComputedStyle(document.documentElement).getPropertyValue(name);
        val = val ? ('' + val).trim() : '';
        if (!val) return fallback;
        if (/ms$/.test(val)) return parseFloat(val) / 1000; // ms -> s
        if (/s$/.test(val)) return parseFloat(val);
        return val;
    }

    function getDuration(el) {
        var attr = el.getAttribute('data-anim-duration');
        if (attr !== null && attr !== undefined && attr !== '') {
            return parseFloat(attr) / 1000; // ms -> s
        }
        return Number(cssVar('--anim-duration', 0.88));
    }

    function getEase(el) {
        var e = el.getAttribute('data-anim-ease');
        return e || cssVar('--anim-ease', 'power2.out');
    }

    // Variant map -> starting states (FROM values)
    var variants = {
        'fade': function () { return { opacity: 0 }; },
        'fade-up': function () { return { opacity: 0, y: parseFloat(cssVar('--anim-shift-s', 16)) }; },
        'fade-down': function () { return { opacity: 0, y: -parseFloat(cssVar('--anim-shift-s', 16)) }; },
        'slide-left': function () { return { opacity: 0, x: parseFloat(cssVar('--anim-shift-m', 32)) }; },
        'slide-right': function () { return { opacity: 0, x: -parseFloat(cssVar('--anim-shift-m', 32)) }; },
        'slide-up': function () { return { opacity: 0, y: parseFloat(cssVar('--anim-shift-m', 32)) }; },
        'slide-down': function () { return { opacity: 0, y: -parseFloat(cssVar('--anim-shift-m', 32)) }; },
        'scale-in': function () { return { opacity: 0, scale: 0.96 }; },
        'zoom-in': function () { return { scale: 1 }; }
    };

    // Optional overrides for the TO end-state (variants that don't animate back to scale:1 / opacity:1 defaults)
    var toOverrides = {
        'zoom-in': { scale: 1.03 }
    };

    // Stagger variant map -> starting states for CHILDREN
    var staggerFromStates = {
        'stagger-fade-up':     function () { return { opacity: 0, y:  parseFloat(cssVar('--anim-shift-s', 16)) }; },
        'stagger-fade-down':   function () { return { opacity: 0, y: -parseFloat(cssVar('--anim-shift-s', 16)) }; },
        'stagger-slide-left':  function () { return { opacity: 0, x:  parseFloat(cssVar('--anim-shift-m', 32)) }; },
        'stagger-slide-right': function () { return { opacity: 0, x: -parseFloat(cssVar('--anim-shift-m', 32)) }; },
        'stagger-fade':        function () { return { opacity: 0 }; }
    };

    function buildTweenCfg(el, delayMs) {
        var d = (parseInt(delayMs, 10) || 0) / 1000;
        return {
            opacity: 1, x: 0, y: 0, scale: 1,
            duration: getDuration(el),
            ease: getEase(el),
            delay: d
        };
    }

    // Trigger helper: supports once/by-threshold and animates on enter + enterBack
    function makeTrigger(el, onEnter) {
        var onceAttr = el.getAttribute('data-anim-once');
        var once = (onceAttr === null || onceAttr === undefined || onceAttr === '') ? true : (onceAttr === 'true');
        var threshold = parseFloat(el.getAttribute('data-anim-threshold') || '0.15');
        var startPct = Math.round((1 - threshold) * 100); // e.g. 85
        return _ScrollTrigger.create({
            trigger: el,
            start: 'top ' + startPct + '%',
            once: once,
            onEnter: onEnter,
            onEnterBack: onEnter
        });
    }

    // MAIN: animate a single element
    function animateElement(el) {
        var variantName = el.getAttribute('data-anim') || 'fade';
        var variantFn = variants[variantName] || variants.fade;
        var delay = el.getAttribute('data-anim-delay') || '0';

        if (isReduced()) {
            _gsap.set(el, { opacity: 1, clearProps: 'all' });
            return;
        }

        // Stagger mode: any recognised stagger-* variant or explicit data-anim-stagger="children"
        var staggerFromFn = staggerFromStates[variantName];
        var staggerChildren = !!staggerFromFn || (el.getAttribute('data-anim-stagger') === 'children');
        if (staggerChildren) {
            var kids = Array.prototype.slice.call(el.children || []);
            if (kids.length) {
                var kidsFrom = staggerFromFn ? staggerFromFn() : { opacity: 0, y: parseFloat(cssVar('--anim-shift-s', 16)) };
                _gsap.set(kids, kidsFrom);
                var amount = parseFloat(el.getAttribute('data-anim-stagger-amount') || '0.08');
                var cfgList = buildTweenCfg(el, delay);
                makeTrigger(el, function () {
                    cfgList.stagger = amount;
                    _gsap.to(kids, cfgList);
                });
            }
            return;
        }

        // Normal variants: prep the element, then animate it
        var fromState = variantFn(el);
        _gsap.set(el, fromState);
        var cfgSingle = buildTweenCfg(el, delay);
        // Merge any custom TO end-state for this variant
        if (toOverrides[variantName]) {
            var overrides = toOverrides[variantName];
            for (var key in overrides) {
                if (overrides.hasOwnProperty(key)) cfgSingle[key] = overrides[key];
            }
        }
        makeTrigger(el, function () { _gsap.to(el, cfgSingle); });
    }

    // Parallax
    function initParallax(el) {
        var depth = parseFloat(el.getAttribute('data-parallax') || '0');
        if (!isFinite(depth)) return;

        var allowMobileAttr = el.getAttribute('data-parallax-mobile');
        var allowMobile = (allowMobileAttr === 'true');
        if (isReduced() || (!allowMobile && isMobile())) return;

        var maxPx = parseFloat(el.getAttribute('data-parallax-max') || '48');
        var shift = depth * maxPx;
        if (shift > maxPx) shift = maxPx;
        if (shift < -maxPx) shift = -maxPx;

        _gsap.fromTo(
            el,
            { y: -shift },
            {
                y: shift,
                ease: 'none',
                scrollTrigger: {
                    trigger: el,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true
                }
            }
        );
    }

    function initAnimations(root) {
        var ctx = root || document;

        var animEls = ctx.querySelectorAll('[data-anim]');
        for (var i = 0; i < animEls.length; i++) {
            animateElement(animEls[i]);
        }

        var parallaxEls = ctx.querySelectorAll('[data-parallax]');
        for (var j = 0; j < parallaxEls.length; j++) {
            initParallax(parallaxEls[j]);
        }
    }

    // Expose globally for non-module usage
    window.animEngine = { initAnimations: initAnimations };
})();
